import Head from 'next/head';
import NextLink from 'next/link';
import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Grid, Divider, Chip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import BaseLayout from 'layouts/BaseLayout';
import Header from 'components/Templates/Header';
import AboutSection from 'components/Templates/AboutSection';
import TemplatesService from 'services/TemplatesService';
import buildTemplateImageUrl from 'utils/buildTemplateImageUrl';
import Breadcrumb from 'components/Templates/Breadcrumb';
import List from 'components/Templates/List';
import useIsMobile from 'hooks/useIsMobile';
import useCreationCreate from 'hooks/useCreationCreate';
import { useRouter } from 'next/router';
import { useCreations } from 'contexts/useCreations';
import { PlanEditableCreationsEnum } from 'enums';
import { blackNovemberPromotionData, isAnniversaryActive } from 'utils';
import { COLOR_GRAY_70, COLOR_PURPLE_HOVER, COLOR_WHITE } from 'styles/variables/colors';
import SideCardTemplate from 'components/SideCardTemplate';
import { useTemplate } from 'contexts/TemplateContext';
import BlackNovemberLimitModal from 'components/MyCreations/BlackNovemberLimitModal';
import AnniversaryMindTheGraphModal from 'components/MyCreations/AnniversaryMindTheGraphModal';
import LimitationModal from 'components/MyCreations/LimitationModal';
import { useAuthUser } from 'features/auth/AuthUserContext';

export const useStyles = makeStyles((theme) => ({
  imageBox: {
    textAlign: 'center',

    '& img': {
      maxWidth: '100%',
      maxHeight: 700,
    },
  },
  title: {
    color: '#D6246E',
  },
  divider: {
    backgroundColor: '#D6246E',
  },
  chip: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    background: 'none',
    border: `1px solid ${theme.palette.grey[400]}`,
  },
  descriptionBox: {
    marginTop: theme.spacing(4),
    border: '1px solid #EEEEEE',
    padding: theme.spacing(2, 3),
    borderRadius: 4,
    '& p': {
      fontFamily: 'Lato',
      fontSize: 14,
      color: COLOR_GRAY_70,
      fontWeight: 700,
    },
    '& li': {
      fontFamily: 'Lato',
      color: COLOR_GRAY_70,
      fontSize: 14,
    },
  },
  descriptionTemplate: {
    fontFamily: 'Lato',
    color: COLOR_GRAY_70,
    fontSize: 14,
  },
}));

export function getStaticPaths() {
  return { paths: [], fallback: 'blocking' };
}

export async function getStaticProps({ params: { slug } }) {
  try {
    const { data: template } = await TemplatesService.fetchOne(slug);
    const { data: relatedTemplates } = await TemplatesService.fetchAll({
      search: template.tags,
      formatId: template.formats[0]?.id,
      page: 1,
      perPage: 5,
      neutral: template.neutral,
    });

    return {
      props: {
        template,
        relatedTemplates: {
          ...relatedTemplates,
          data: relatedTemplates.data.filter(
            (relatedTemplate) => relatedTemplate.theme_id !== template.id,
          ),
        },
        title: slug,
      },
      revalidate: 604800, // 1 week
    };
  } catch (e) {
    console.error(e);

    return {
      props: {
        template: null,
        relatedTemplates: {},
        title: slug,
      },
      revalidate: 604800, // 1 week
    };
  }
}

export default function TemplatePage({ template, relatedTemplates }) {
  const classes = useStyles();
  const router = useRouter();
  const isMobile = useIsMobile();
  const { user } = useAuthUser();
  const [allCreations] = useCreations();
  const { createCreation } = useCreationCreate();
  const [isBusy, setIsBusy] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const { setTemplate } = useTemplate();
  const promotionBlacknovember = blackNovemberPromotionData();

  let splitTags = template.tags.split(',');

  const schemaDataImageObject = {
    '@context': 'https://schema.org',
    '@type': 'ImageObject',
    name: `${template.name}`,
    description: `The ${template.name} is a great option to highlight your paper's main subject straight away. Illustrate your science, spread your knowledge, and reach a greater audience with the power of infographics.`,
    contentUrl: `${buildTemplateImageUrl(template.slug, template.imagePrefix, true)}`,
    copyrightNotice: `© ${new Date().getFullYear()} MindtheGraph. All rights reserved.`,
    width: template.width,
    height: template.height,
    creator: {
      '@type': 'Organization',
      name: 'MindtheGraph',
    },
    creditText: 'MindtheGraph',
    datePublished: '2023-07-21',
    uploadDate: '2023-07-21',
    license: 'https://trust.editage.com/terms-of-use.html',
  };

  function getBreadcrumbItems() {
    if (template.neutral) {
      if (template.formats[0]) {
        return [
          { label: 'Neutral Templates', path: '/infographic-templates/category/neutral' },
          {
            label: template.formats[0].name,
            path: `/infographic-templates/category/neutral/format/${template.formats[0].slug}`,
          },
          { label: template.name },
        ];
      }

      return [
        { label: 'Neutral Templates', path: '/infographic-templates/category/neutral' },
        { label: template.name },
      ];
    }

    if (template.formats[0]) {
      return [
        { label: 'Templates', path: '/infographic-templates' },
        {
          label: template.formats[0].name,
          path: `/infographic-templates/format/${template.formats[0].slug}`,
        },
        {
          label: template.name,
          path: router.asPath,
        },
        { label: template.name },
      ];
    }

    return [{ label: 'Templates', path: '/infographic-templates' }, { label: template.name }];
  }

  function handleRedirectToOldWorkspace() {
    window.location.href = `${process.env.MTG_URL_LEGACY}/workspace/create_base_slide/${template.slug}/${template.width}/${template.height}`;
  }

  async function handleTemplateLinkClick() {
    if (allCreations.length >= PlanEditableCreationsEnum[user.tp_user]) {
      setLimitModalOpen(true);

      return;
    }

    if (!user.useNewWorkspace) {
      handleRedirectToOldWorkspace();

      return;
    }

    try {
      setIsBusy(true);

      await createCreation({ themeSlug: template.slug, isTemplatePage: true });
    } catch (e) {
      console.error(e);
    } finally {
      setIsBusy(false);
    }
  }

  useEffect(() => {
    setTemplate(template);
  }, [setTemplate, template]);

  return (
    <BaseLayout
      bgBrandColor={!isMobile ? COLOR_WHITE : COLOR_PURPLE_HOVER}
      headerWhite={true}
      typeBtn="buttonSecondary"
      hasPadding={false}
      context="templateLoginFlow"
    >
      <Head>
        <title>{`${template.name} - Infographic templates`}</title>

        <meta
          key="og:description"
          name="og:description"
          property="og:description"
          content={`This fully customizable ${template.name} infographic template can be just what you need to communicate your data, try it now! It's free.`}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaDataImageObject) }}
        />
      </Head>

      {promotionBlacknovember.isActive && (
        <BlackNovemberLimitModal open={limitModalOpen} onClose={() => setLimitModalOpen(false)} />
      )}

      {isAnniversaryActive() && (
        <AnniversaryMindTheGraphModal
          open={limitModalOpen}
          onClose={() => setLimitModalOpen(false)}
        />
      )}

      {!promotionBlacknovember.isActive && !isAnniversaryActive() && (
        <LimitationModal open={limitModalOpen} onClose={() => setLimitModalOpen(false)} />
      )}

      <Header />

      <Box py={6} bgcolor="common.white">
        <Container>
          <Box mb={4}>
            <Breadcrumb items={getBreadcrumbItems()} />
          </Box>

          <Grid container={true} spacing={4}>
            <Grid item={true} xs={12} md={8}>
              <div className={classes.imageBox}>
                <img
                  src={buildTemplateImageUrl(template.slug, template.imagePrefix, true)}
                  alt={
                    template.formats[0]?.name
                      ? `${template.formats[0]?.name} template ${template.name}`
                      : template.name
                  }
                />
              </div>
            </Grid>

            <Grid item={true} xs={12} md={4}>
              <SideCardTemplate isBusy={isBusy} onOpenTemplate={handleTemplateLinkClick} />
            </Grid>
          </Grid>

          <Grid container={true}>
            <Grid item={true} xs={12} md={8}>
              <Box px={!isMobile ? 8 : 1} pt={3} className={classes.descriptionBox}>
                <Typography variant="body1">Details</Typography>
                <ul>
                  {template.canvasSize?.width && template.canvasSize?.height && (
                    <li>
                      <b>Size:</b> {template.canvasSize.width + 'x'}
                      {template.canvasSize.height}
                    </li>
                  )}
                </ul>

                <Typography className={classes.descriptionTemplate}>
                  The <b>{template.name}</b> is a great option to highlight your paper's main
                  subject straight away. Illustrate your science, spread your knowledge, and reach a
                  greater audience with the power of infographics. Remember you can custom,
                  download, present and share your work in just a few clicks. This template was made
                  by scientists for scientists.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {!!relatedTemplates.data.length && (
            <Grid container={true}>
              <Grid item={true} xs={12} md={12}>
                <Box my={4}>
                  <Divider />
                </Box>
                <Typography variant="body1" mb={1}>
                  <b>Related templates</b>
                </Typography>

                <List
                  breadcrumbItems={[{ label: 'Related Templates' }]}
                  formatName={template.formats[0]?.name}
                  templates={relatedTemplates}
                  columns={5}
                />
              </Grid>
            </Grid>
          )}

          <Box my={4}>
            <Divider />
          </Box>
          <Grid container={true}>
            <Grid item={true} xs={12} md={12}>
              <Typography variant="body1">
                <b>Tags</b>
              </Typography>

              <Box py={2}>
                {splitTags.map((tag) => (
                  <NextLink
                    key={tag}
                    passHref={true}
                    href={`/infographic-templates/search?search=${encodeURIComponent(tag.trim())}`}
                  >
                    <Chip id={tag.trim()} className={classes.chip} label={tag.trim()} />
                  </NextLink>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <AboutSection />
    </BaseLayout>
  );
}
